import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { HelpCircle } from "lucide-react";
import ContactBlock from "../../components/contact/ContactBlock";

const GuideScreen = () => {
	const helpers = [
		{
			name: "Fabric Selection Guide",
			icon: "/images/newHome/fabric-selection-logo-120x120.png",
			link: "/guides/productselection",
			description: "Find your perfect window treatment fabric",
		},
		{
			name: "Measuring Guides",
			icon: "/images/newHome/measuring-guide-logo-120x120.png",
			link: "/guides/measuring-guide",
			description: "Get precise measurements every time",
		},
		{
			name: "Installation Guides",
			icon: "/images/newHome/installation-guides-120x120.png",
			link: "/guides/installation-guide",
			description: "Step-by-step installation instructions",
		},
		{
			name: "Shipping Information",
			icon: "/images/newHome/shipping-information-120x120.png",
			link: "/guides/shipping-policy",
			description: "Delivery details and tracking",
		},
		{
			name: "Return Policy",
			icon: "/images/newHome/return-policy-120x120.png",
			link: "/guides/return-policy",
			description: "Hassle-free returns process",
		},
		{
			name: "Warranty",
			icon: "/images/newHome/warranty-policy-120x120.png",
			link: "/guides/warranty",
			description: "Product warranty coverage details",
		},
	];

	// Animation constants matching home page
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	return (
		<div className="overflow-x-hidden">
			<Helmet>
				<title>Guides | Off Cut Shades</title>
				<meta
					name="description"
					content="Get help with fabric selection, measuring, installation, shipping, returns, and warranty information."
				/>
				<link rel="canonical" href="https://offcutshades.com/help" />
			</Helmet>

			<main className="bg-gradient-to-b from-white to-gray-50">
				{/* Hero Section */}
				<section className="container mx-auto px-4 pt-12 pb-24">
					<motion.div
						className="relative text-center max-w-3xl mx-auto mb-16 z-10"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={bounceTransition}
					>
						<motion.h1
							className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.2 }}
						>
							Guides
						</motion.h1>
						<motion.p
							className="text-xl text-gray-600"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.3 }}
						>
							Find everything you need to know about our products and services
						</motion.p>
					</motion.div>

					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						{helpers.map((item, index) => (
							<motion.div
								key={index}
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ ...bounceTransition, delay: index * 0.1 }}
								viewport={{ once: true }}
							>
								<Link to={item.link} className="block">
									<motion.div
										className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all backdrop-blur-sm"
										whileHover={{ y: -10, scale: 1.02 }}
									>
										<div className="flex flex-col items-center text-center">
											<div className="w-24 h-24 mb-6 rounded-2xl overflow-hidden bg-primary/5 p-4">
												<img
													src={item.icon}
													alt={item.name}
													className="w-full h-full object-contain"
												/>
											</div>
											<h2 className="text-2xl font-bold mb-3">{item.name}</h2>
											<p className="text-gray-600">{item.description}</p>
										</div>
									</motion.div>
								</Link>
							</motion.div>
						))}
					</div>
				</section>

				{/* Contact Support Section */}
				<ContactBlock />
			</main>
		</div>
	);
};

export default GuideScreen;
